import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createBrowserHistory, createRouter } from '@tanstack/react-router';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { flushSync } from 'react-dom';
import { createRoot, type Root } from 'react-dom/client';
import {
  type AppBootstrapContext,
  type AppRuntime,
  type BootstrapContextValue,
  type MemberManagementReactAppHandle,
  type MemberManagementReactAppOptions,
  type WorkspaceContextValue
} from './reactApp/appTypes';
import { AppRuntimeContext, useBootstrapContext, useWorkspaceContext } from './reactApp/context';
import { MemberGroupCreateDraftStoreProvider, ScheduleDraftStoreProvider } from './reactApp/draftStores';
import { routeTree } from './reactApp/routes';
import { createNoopUxMetricsPort } from './uxMetrics';

export type {
  AppBootstrapContext,
  BootstrapContextValue,
  MemberManagementReactAppHandle,
  MemberManagementReactAppOptions,
  WorkspaceContextValue
};
export { useBootstrapContext, useWorkspaceContext };

export function mountMemberManagementReactApp(
  target: HTMLElement,
  options: MemberManagementReactAppOptions
): MemberManagementReactAppHandle {
  target.replaceChildren();
  const root = createRoot(target);
  flushSync(() => {
    root.render(<MemberManagementReactApp {...options} />);
  });

  return {
    unmount: () => {
      root.unmount();
    }
  };
}

export function MemberManagementReactApp(options: MemberManagementReactAppOptions): ReactNode {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: false
          }
        }
      })
  );
  const [routerHistory] = useState(() => options.routerHistory ?? createBrowserHistory());
  const [router] = useState(() =>
    createRouter({
      routeTree,
      history: routerHistory,
      scrollRestoration: () => false
    })
  );
  useEffect(() => {
    options.testHooks?.onQueryClient?.(queryClient);
  }, [options.testHooks, queryClient]);
  const runtime = useMemo<AppRuntime>(
    () => ({
      service: options.service,
      resetLocalMvpData: options.resetLocalMvpData,
      uxMetrics: options.uxMetrics ?? createNoopUxMetricsPort(),
      routerHistory
    }),
    [options.resetLocalMvpData, options.service, options.uxMetrics, routerHistory]
  );

  return (
    <AppRuntimeContext.Provider value={runtime}>
      <QueryClientProvider client={queryClient}>
        <ScheduleDraftStoreProvider>
          <MemberGroupCreateDraftStoreProvider>
            <RouterProvider router={router} />
          </MemberGroupCreateDraftStoreProvider>
        </ScheduleDraftStoreProvider>
      </QueryClientProvider>
    </AppRuntimeContext.Provider>
  );
}

export type MemberManagementReactRoot = Root;
